/**
 * Case-study lightbox.
 *
 * Makes the story panels in the "about the project" section (the full-width
 * figures inside `.case-story`) clickable so visitors can open each panel at
 * full size. Scroll-lock, focus handling, and Escape-to-close mirror the
 * schedule-call modal so behaviour stays consistent across the site.
 */

/* Story panels: standard case pages use `.case-story__media`; the fyio page
 * uses its own conclusion grid. Both hold the "about the project" images. */
const IMAGE_SELECTOR =
  ".case-story__media img, .dex-fyio-conclusion-grid__cell img, .dex-fyio-showcase__phones img";

/* Only these projects get clickable story images. Stored without the `.html`
   extension so it matches both local `case-val.html` and Netlify's clean
   `/case-val` prod URLs. */
const ALLOWED_PAGES = new Set([
  "case-fyio",
  "case-imployable",
  "case-val",
  "case-luxembourg", // NEOBION
  "case-healthcare-portfolio", // healthcare & pharma
]);

function currentPageIsAllowed(): boolean {
  const file = (window.location.pathname.split("/").pop() || "").replace(
    /\.html$/,
    ""
  );
  return ALLOWED_PAGES.has(file);
}

function initCaseLightbox(): void {
  if (!currentPageIsAllowed()) return;

  const images = Array.from(
    document.querySelectorAll<HTMLImageElement>(IMAGE_SELECTOR)
  );
  if (!images.length) return;

  let lockedScrollY = 0;
  let lastFocus: HTMLElement | null = null;

  const overlay = document.createElement("div");
  overlay.className = "dex-lightbox";
  overlay.setAttribute("aria-hidden", "true");
  overlay.innerHTML = `
    <div class="dex-lightbox__scrim" data-lightbox-close></div>
    <figure class="dex-lightbox__figure" role="dialog" aria-modal="true" aria-label="Enlarged case-study image">
      <button type="button" class="dex-lightbox__close" aria-label="Close image">&times;</button>
      <img class="dex-lightbox__img" alt="" />
    </figure>
  `;
  document.body.appendChild(overlay);

  const overlayImg = overlay.querySelector<HTMLImageElement>(".dex-lightbox__img");
  const closeBtn = overlay.querySelector<HTMLButtonElement>(".dex-lightbox__close");
  if (!overlayImg || !closeBtn) return;

  const lockScroll = () => {
    lockedScrollY = window.scrollY;
    document.documentElement.classList.add("dex-lightbox-open");
    document.body.classList.add("dex-lightbox-open");
    document.body.style.position = "fixed";
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  };

  const unlockScroll = () => {
    document.documentElement.classList.remove("dex-lightbox-open");
    document.body.classList.remove("dex-lightbox-open");
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    window.scrollTo(0, lockedScrollY);
  };

  const openLightbox = (source: HTMLImageElement) => {
    lastFocus = document.activeElement as HTMLElement | null;
    overlayImg.src = source.currentSrc || source.src;
    overlayImg.alt = source.alt || "";
    lockScroll();
    overlay.classList.add("is-open");
    overlay.setAttribute("aria-hidden", "false");
    requestAnimationFrame(() => closeBtn.focus());
  };

  const closeLightbox = () => {
    if (!overlay.classList.contains("is-open")) return;
    overlay.classList.remove("is-open");
    overlay.setAttribute("aria-hidden", "true");
    unlockScroll();
    if (lastFocus && typeof lastFocus.focus === "function") lastFocus.focus();
    lastFocus = null;
  };

  images.forEach((img) => {
    img.classList.add("dex-case-img--zoomable");
    img.setAttribute("role", "button");
    img.setAttribute("tabindex", "0");
    if (!img.getAttribute("aria-label")) {
      img.setAttribute("aria-label", `${img.alt || "Case study image"} - open full size`);
    }
    img.addEventListener("click", () => openLightbox(img));
    img.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      openLightbox(img);
    });
  });

  overlay.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).closest("[data-lightbox-close]")) closeLightbox();
  });
  closeBtn.addEventListener("click", () => closeLightbox());

  document.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key !== "Escape" || !overlay.classList.contains("is-open")) return;
    e.preventDefault();
    closeLightbox();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCaseLightbox);
} else {
  initCaseLightbox();
}

export {};
